import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { COMMA, ENTER, FF_SEMICOLON, SEMICOLON } from '@angular/cdk/keycodes';

import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PerfilAutorizadoListaComponent } from '@shared/components/perfil-autorizado-lista/perfil-autorizado-lista.component';
import { PageScrollSpyComponent, ScrollSpySection } from '@shared/components/page-scroll-spy/page-scroll-spy.component';

import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { PerfilService } from '@shared/services/apis/usuario/perfil.service';
import { ConfiguracaoNFeService } from '@shared/services/apis/dfe/configuracao-nfe.service';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { ReportLookupService } from '@shared/services/apis/report/report-lookup.service';
import { ConfiguracaoNFeStruct } from '@shared/services/structs/dfe/configuracao-nfe.struct';
import { ConfiguracaoNFeRequest } from '@shared/services/requests/dfe/configuracao-nfe.request';
import { TipoFonteDanfe } from '@shared/services/models/report/tipo-fonte-danfe.model';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { PerfilOptionStruct } from '@shared/services/structs/usuario/perfil-option.struct';
import { documentoValidator } from '@shared/validators/documento.validator';
import { emailValidator } from '@shared/validators/email.validator';
import { CpfCnpjPipe } from '@shared/pipes/cpf-cnpj.pipe';

/** Linha editável da lista de autorizados a baixar o XML (dado local de tela). */
type AutorizadoXmlLinha = {
  /** Documento como exibido no campo (formatado depois de validado). */
  documento: string;
  nome: string;
  erroDocumento: string;
  erroNome: string;
};

const LIMITE_AUTORIZADOS_XML = 10;

@Component({
  selector: 'app-config-nfe',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
    EmptyStateComponent,
    PerfilAutorizadoListaComponent,
    PageScrollSpyComponent,
  ],
  templateUrl: './config-nfe.component.html',
  styleUrls: ['./config-nfe.component.css'],
})
export class ConfigNFeComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;
  public readonly limiteAutorizadosXml = LIMITE_AUTORIZADOS_XML;
  public readonly separadoresEmail = [ENTER, COMMA, SEMICOLON, FF_SEMICOLON];
  public readonly colunasAutorizados = ['documento', 'nome', 'acoes'];

  public sections: ScrollSpySection[] = [
    { id: 'padroes-documento', label: 'Padrões do documento' },
    { id: 'observacoes-automaticas', label: 'Observações automáticas' },
    { id: 'validacoes-bloqueios', label: 'Validações e bloqueios' },
    { id: 'campos-segmento', label: 'Campos por segmento' },
    { id: 'envio-compartilhamento', label: 'Envio e compartilhamento' },
    { id: 'danfe-impressao', label: 'DANFE e impressão' },
  ];

  public podeVisualizar = false;
  public podeAlterar = false;

  public isLoading = false;
  public isSaving = false;
  public configCarregada = false;

  public form: FormGroup;

  // Listas fora do FormGroup: marcam o form como alterado ao mudar (padrão de config-geral-vendas).
  public emailsCopia: string[] = [];
  public erroEmail = '';
  public autorizadosXml: AutorizadoXmlLinha[] = [];
  public idsPerfilLiberacaoDivergencia: number[] = [];

  public opcoesIndicadorPresenca: LookupItemStruct[] = [];
  public opcoesModalidadeFrete: LookupItemStruct[] = [];
  public opcoesFonteDanfe: TipoFonteDanfe[] = [];
  public opcoesPerfis: PerfilOptionStruct[] = [];

  private readonly cpfCnpjPipe = new CpfCnpjPipe();

  constructor(
    private formBuilder: FormBuilder,
    private configuracaoNFeService: ConfiguracaoNFeService,
    private motorFiscalLookupService: MotorFiscalLookupService,
    private reportLookupService: ReportLookupService,
    private perfilService: PerfilService,
    private authService: AuthService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.form = this.formBuilder.group({
      // Padrões do documento
      idIndicadorPresencaComprador: [null as number | null, Validators.required],
      idModalidadeFrete: [null as number | null, Validators.required],
      especieVolumePadrao: ['', Validators.maxLength(60)],
      isSubstituirEnderecoEntrega: [false],
      // Observações automáticas
      isObservacaoOrdemCompra: [false],
      isObservacaoNumeroVenda: [false],
      // Validações e bloqueios
      isValidarNcm: [false],
      isBloquearEdicaoNumero: [false],
      isBloquearItensDiferentesVenda: [false],
      // Campos por segmento de atuação
      isExibirDefensivosAgricolas: [false],
      isExibirGuiaTransito: [false],
      // DANFE e impressão
      idTipoFonteDanfe: [null as number | null, Validators.required],
      isRemoverEnderecoDestinatarioEtiqueta: [false],
      isRemoverEnderecoEmitenteEtiqueta: [false],
      isRemoverValorTotalEtiqueta: [false],
      isRemoverGtinDanfe: [false],
    });
  }

  ngOnInit(): void {
    const permissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];
    this.podeVisualizar = permissoes.includes(PermissaoEnum.Configuracoes_NotasFiscais_Visualizar);
    this.podeAlterar = permissoes.includes(PermissaoEnum.Configuracoes_NotasFiscais_Alterar);
    if (!this.podeAlterar) this.form.disable();

    if (this.podeVisualizar) this.carregar();
  }

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isDirty) event.preventDefault();
  }

  public carregar(): void {
    this.isLoading = true;
    this.configCarregada = false;
    forkJoin({
      config: this.configuracaoNFeService.get().pipe(catchError(error => of({ falha: error?.message as string }))),
      fonteDanfe: this.reportLookupService.getTiposFonteDanfe().pipe(catchError(error => {
        this.alertService.show('Erro', error?.message || 'Falha ao carregar as opções da tela.', AlertType.error);
        return of(null);
      })),
      presenca: this.motorFiscalLookupService.getIndicadoresPresencaComprador().pipe(catchError(error => {
        this.alertService.show('Erro', error?.message || 'Não foi possível carregar os indicadores de presença.', AlertType.error);
        return of([] as LookupItemStruct[]);
      })),
      frete: this.motorFiscalLookupService.getModalidadesFrete().pipe(catchError(error => {
        this.alertService.show('Erro', error?.message || 'Não foi possível carregar as modalidades de frete.', AlertType.error);
        return of([] as LookupItemStruct[]);
      })),
      perfis: this.perfilService.getAllLookup().pipe(catchError(error => {
        this.alertService.show('Erro', error?.message || 'Falha ao carregar as opções da tela.', AlertType.error);
        return of(null);
      })),
    }).subscribe(res => {
      this.isLoading = false;

      this.opcoesIndicadorPresenca = res.presenca ?? [];
      this.opcoesModalidadeFrete = res.frete ?? [];
      // O lookup compartilhado converte falhas em lista vazia; estes catálogos são obrigatórios.
      if (this.opcoesIndicadorPresenca.length === 0) {
        this.alertService.show('Aviso', 'Os indicadores de presença não foram carregados.', AlertType.warning);
      }
      if (this.opcoesModalidadeFrete.length === 0) {
        this.alertService.show('Aviso', 'As modalidades de frete não foram carregadas.', AlertType.warning);
      }

      if (res.fonteDanfe && !res.fonteDanfe.isError) {
        this.opcoesFonteDanfe = res.fonteDanfe.listaTipoFonteDanfe ?? [];
      } else if (res.fonteDanfe?.isError) {
        this.alertService.show('Aviso', res.fonteDanfe.errorDescription, AlertType.warning);
      }

      if (res.perfis && !res.perfis.isError) {
        this.opcoesPerfis = (res.perfis.listaPerfil ?? []).map(p => ({ idPerfil: p.perfil.idPerfil, nome: p.perfil.nome }));
      } else if (res.perfis?.isError) {
        this.alertService.show('Aviso', res.perfis.errorDescription, AlertType.warning);
      }

      if ('falha' in res.config) {
        this.form.disable();
        this.alertService.show('Erro', res.config.falha || 'Falha na comunicação com o servidor.', AlertType.error);
        return;
      }
      if (res.config.isError || !res.config.configuracaoNFe) {
        this.form.disable();
        this.alertService.show('Erro', res.config.errorDescription || 'Não foi possível carregar as configurações da NF-e.', AlertType.error);
        return;
      }
      this.aplicarConfiguracao(res.config.configuracaoNFe);
      this.configCarregada = true;
      if (this.podeAlterar) this.form.enable();
    });
  }

  private aplicarConfiguracao(c: ConfiguracaoNFeStruct): void {
    this.form.patchValue({
      idIndicadorPresencaComprador: c.idIndicadorPresencaComprador,
      idModalidadeFrete: c.idModalidadeFrete,
      especieVolumePadrao: c.especieVolumePadrao ?? '',
      isSubstituirEnderecoEntrega: c.isSubstituirEnderecoEntrega,
      isObservacaoOrdemCompra: c.isObservacaoOrdemCompra,
      isObservacaoNumeroVenda: c.isObservacaoNumeroVenda,
      isValidarNcm: c.isValidarNcm,
      isBloquearEdicaoNumero: c.isBloquearEdicaoNumero,
      isBloquearItensDiferentesVenda: c.isBloquearItensDiferentesVenda,
      isExibirDefensivosAgricolas: c.isExibirDefensivosAgricolas,
      isExibirGuiaTransito: c.isExibirGuiaTransito,
      idTipoFonteDanfe: c.idTipoFonteDanfe,
      isRemoverEnderecoDestinatarioEtiqueta: c.isRemoverEnderecoDestinatarioEtiqueta,
      isRemoverEnderecoEmitenteEtiqueta: c.isRemoverEnderecoEmitenteEtiqueta,
      isRemoverValorTotalEtiqueta: c.isRemoverValorTotalEtiqueta,
      isRemoverGtinDanfe: c.isRemoverGtinDanfe,
    });
    this.emailsCopia = [...(c.emailsCopia ?? [])];
    this.autorizadosXml = (c.autorizadosXml ?? []).map(a => ({
      documento: this.cpfCnpjPipe.transform(a.documento),
      nome: a.nome,
      erroDocumento: '',
      erroNome: '',
    }));
    this.idsPerfilLiberacaoDivergencia = [...(c.idsPerfilLiberacaoDivergencia ?? [])];
    this.erroEmail = '';
    this.form.markAsPristine();
  }

  public get isDirty(): boolean {
    return this.form.dirty;
  }

  // ---------------- E-mails em cópia ----------------

  public adicionarEmails(event: MatChipInputEvent): void {
    const digitado = (event.value ?? '').trim();
    if (!digitado) {
      this.erroEmail = '';
      return;
    }

    const invalidos: string[] = [];
    let incluiu = false;
    for (const email of digitado.split(/[,;\s]+/).filter(e => !!e)) {
      if (emailValidator()(new FormControl(email))) {
        invalidos.push(email);
        continue;
      }
      // Endereço já informado é ignorado.
      if (this.emailsCopia.some(e => e.toLowerCase() === email.toLowerCase())) continue;
      this.emailsCopia = [...this.emailsCopia, email];
      incluiu = true;
    }

    event.chipInput.clear();
    if (invalidos.length) {
      // O inválido volta para o campo para ser corrigido.
      event.chipInput.inputElement.value = invalidos.join(', ');
      this.erroEmail = invalidos.length === 1
        ? `O e-mail "${invalidos[0]}" é inválido.`
        : `Os e-mails ${invalidos.map(e => `"${e}"`).join(', ')} são inválidos.`;
    } else {
      this.erroEmail = '';
    }
    if (incluiu) this.form.markAsDirty();
  }

  public removerEmail(index: number): void {
    this.emailsCopia = this.emailsCopia.filter((_, i) => i !== index);
    this.form.markAsDirty();
  }

  // ---------------- Autorizados a baixar o XML ----------------

  public get podeAdicionarAutorizado(): boolean {
    return this.podeAlterar && this.configCarregada && this.autorizadosXml.length < LIMITE_AUTORIZADOS_XML;
  }

  public adicionarAutorizado(): void {
    if (!this.podeAdicionarAutorizado) return;
    this.autorizadosXml = [...this.autorizadosXml, { documento: '', nome: '', erroDocumento: '', erroNome: '' }];
    this.form.markAsDirty();
  }

  public removerAutorizado(index: number): void {
    this.autorizadosXml = this.autorizadosXml.filter((_, i) => i !== index);
    // A remoção pode desfazer uma duplicidade apontada em outra linha.
    this.autorizadosXml.forEach(linha => { if (linha.documento) this.validarDocumento(linha); });
    this.form.markAsDirty();
  }

  public onAutorizadoAlterado(): void {
    this.form.markAsDirty();
  }

  /** Valida ao sair do campo: 11 dígitos (CPF) ou 14 (CNPJ), dígito verificador e duplicidade; formata o valor. */
  public validarDocumento(linha: AutorizadoXmlLinha): void {
    const bruto = this.documentoSemFormatacao(linha.documento);
    if (!bruto) {
      linha.erroDocumento = 'Informe o CPF ou o CNPJ.';
      return;
    }
    if (bruto.length !== 11 && bruto.length !== 14) {
      linha.erroDocumento = 'Informe 11 dígitos para CPF ou 14 para CNPJ.';
      return;
    }
    if (documentoValidator()(new FormControl(bruto))) {
      linha.erroDocumento = bruto.length === 11 ? 'CPF inválido.' : 'CNPJ inválido.';
      return;
    }
    const duplicado = this.autorizadosXml.some(outra => outra !== linha && this.documentoSemFormatacao(outra.documento) === bruto);
    if (duplicado) {
      linha.erroDocumento = 'Documento já informado na lista.';
      return;
    }
    linha.erroDocumento = '';
    linha.documento = this.cpfCnpjPipe.transform(bruto);
  }

  public validarNome(linha: AutorizadoXmlLinha): void {
    linha.erroNome = linha.nome?.trim() ? '' : 'Informe um nome para reconhecer o autorizado.';
  }

  private documentoSemFormatacao(documento: string): string {
    return (documento ?? '').replace(/[.\-\/\s]/g, '').toUpperCase();
  }

  // ---------------- Perfis que liberam a divergência ----------------

  public get isBloqueioItensAtivo(): boolean {
    return !!this.form.get('isBloquearItensDiferentesVenda')?.value;
  }

  public onPerfisLiberacaoChange(ids: number[]): void {
    this.idsPerfilLiberacaoDivergencia = ids;
    this.form.markAsDirty();
  }

  // ---------------- Salvar e voltar ----------------

  public salvar(): void {
    if (!this.podeAlterar || !this.configCarregada || this.isSaving) return;

    this.autorizadosXml.forEach(linha => {
      this.validarDocumento(linha);
      this.validarNome(linha);
    });
    this.form.markAllAsTouched();
    const autorizadosInvalidos = this.autorizadosXml.some(l => l.erroDocumento || l.erroNome);
    if (this.form.invalid || autorizadosInvalidos) {
      this.alertService.show('Atenção', 'Corrija os campos destacados antes de salvar.', AlertType.error);
      return;
    }

    const raw = this.form.getRawValue();
    const request: ConfiguracaoNFeRequest = {
      idIndicadorPresencaComprador: raw.idIndicadorPresencaComprador,
      idModalidadeFrete: raw.idModalidadeFrete,
      especieVolumePadrao: raw.especieVolumePadrao?.trim() || null,
      isSubstituirEnderecoEntrega: raw.isSubstituirEnderecoEntrega,
      isObservacaoOrdemCompra: raw.isObservacaoOrdemCompra,
      isObservacaoNumeroVenda: raw.isObservacaoNumeroVenda,
      isValidarNcm: raw.isValidarNcm,
      isBloquearEdicaoNumero: raw.isBloquearEdicaoNumero,
      isBloquearItensDiferentesVenda: raw.isBloquearItensDiferentesVenda,
      isExibirDefensivosAgricolas: raw.isExibirDefensivosAgricolas,
      isExibirGuiaTransito: raw.isExibirGuiaTransito,
      emailsCopia: this.emailsCopia,
      idTipoFonteDanfe: raw.idTipoFonteDanfe,
      isRemoverEnderecoDestinatarioEtiqueta: raw.isRemoverEnderecoDestinatarioEtiqueta,
      isRemoverEnderecoEmitenteEtiqueta: raw.isRemoverEnderecoEmitenteEtiqueta,
      isRemoverValorTotalEtiqueta: raw.isRemoverValorTotalEtiqueta,
      isRemoverGtinDanfe: raw.isRemoverGtinDanfe,
      autorizadosXml: this.autorizadosXml.map(l => ({ documento: this.documentoSemFormatacao(l.documento), nome: l.nome.trim() })),
      // Os perfis ficam guardados mesmo com o bloqueio inativo, para quando ele for religado.
      idsPerfilLiberacaoDivergencia: this.idsPerfilLiberacaoDivergencia,
    };

    this.isSaving = true;
    this.configuracaoNFeService.atualizar(request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          // Mantém o que foi digitado; nova tentativa pelo próprio Salvar.
          this.alertService.show('Erro', response.errorDescription || 'Não foi possível salvar as configurações da NF-e.', AlertType.error);
          return;
        }
        if (response.configuracaoNFe) this.aplicarConfiguracao(response.configuracaoNFe);
        else this.form.markAsPristine();
        this.alertService.show(
          'Sucesso',
          'Configurações da NF-e salvas. Os novos valores valem para as próximas notas.',
          AlertType.success
        );
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Erro', error?.message || 'Falha na comunicação com o servidor.', AlertType.error);
      },
    });
  }

  public voltar(): void {
    const destino = '/configuracoes/notas-fiscais';
    if (!this.isDirty) {
      this.router.navigate([destino]);
      return;
    }
    this.dialog.open(ConfirmModalComponent, {
      maxWidth: '96vw',
      data: {
        title: 'Descartar alterações?',
        description: 'Há alterações não salvas nas configurações da NF-e. Deseja sair mesmo assim?',
        buttonConfirm: 'Descartar',
        buttonClose: 'Voltar',
      },
    }).afterClosed().subscribe(result => {
      if (result?.confirm) {
        // Evita o aviso do navegador ao sair depois de confirmar.
        this.form.markAsPristine();
        this.router.navigate([destino]);
      }
    });
  }
}
